import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { format, eachDayOfInterval } from 'date-fns';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const getContrastColor = (hexColor) => {
  if (!hexColor) return '#fff';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
};

const BookingReport = () => {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-01'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchName, setSearchName] = useState('');

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelStartDate, setCancelStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [cancelEndDate, setCancelEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/report', { params: { startDate, endDate } });
      setReport(res.data);
      setSearched(true);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const handleCancelConfirm = async () => {
    setCancelLoading(true);
    setCancelResult(null);
    try {
      const res = await api.delete('/bookings/range', { data: { startDate: cancelStartDate, endDate: cancelEndDate } });
      setCancelResult({ success: true, message: res.data.message, count: res.data.count });
      if (searched) fetchReport();
    } catch (err) {
      setCancelResult({ success: false, message: err.response?.data?.message || 'Failed to cancel bookings' });
    } finally {
      setCancelLoading(false);
    }
  };

  const filteredReport = searchName
    ? report.filter(r => r.name.toLowerCase().includes(searchName.toLowerCase()) || (r.abbreviation || '').toLowerCase().includes(searchName.toLowerCase()))
    : report;
  const totalBookings = filteredReport.reduce((sum, r) => sum + (r.total_bookings || 0), 0);
  const activeLeaders = filteredReport.filter(r => r.total_bookings > 0).length;

  const exportExcel = () => {
    const allDates = eachDayOfInterval({
      start: new Date(startDate + 'T00:00:00'),
      end: new Date(endDate + 'T00:00:00'),
    });

    const data = filteredReport.map(row => {
      const bookedSet = new Set(
        row.booking_dates ? row.booking_dates.split(',').map(d => d.trim()) : []
      );
      const rowData = {
        'Team Leader': row.name,
        'Abbreviation': row.abbreviation || '',
        'Team Size': row.team_size || 0,
        'Total Bookings': row.total_bookings || 0,
        'Total Days': row.total_days || 0,
      };
      allDates.forEach(date => {
        rowData[format(date, 'dd/MM/yyyy')] = bookedSet.has(format(date, 'yyyy-MM-dd')) ? 'Yes' : 'No';
      });
      return rowData;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.max(key.length, ...data.map(r => String(r[key] || '').length)) + 2,
    }));
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Booking Report');
    XLSX.writeFile(wb, `booking-report-${startDate}-to-${endDate}.xlsx`);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#052e2b' }}>
        Booking Report
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={fetchReport}
            disabled={loading || !startDate || !endDate}
            sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Report'}
          </Button>
          {searched && (
            <TextField
              label="Search Leader"
              size="small"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Name or abbreviation"
              sx={{ width: 200 }}
            />
          )}
          {searched && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip label={`${activeLeaders} active leader(s)`} size="small" color="primary" />
              <Chip label={`${totalBookings} total booking(s)`} size="small" variant="outlined" />
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={exportExcel}
                sx={{ borderColor: '#052e2b', color: '#052e2b' }}
              >
                Export Excel
              </Button>
            </Box>
          )}
          {user?.role === 'admin' && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => { setCancelResult(null); setCancelDialogOpen(true); }}
              sx={{ ml: 'auto' }}
            >
              Cancel All Bookings
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog open={cancelDialogOpen} onClose={() => !cancelLoading && setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 600 }}>
          Cancel All Bookings
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently cancel <strong>all bookings</strong> within the selected date range for all team leaders. This action cannot be undone.
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={cancelStartDate}
              onChange={(e) => { setCancelStartDate(e.target.value); setCancelResult(null); }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={cancelEndDate}
              onChange={(e) => { setCancelEndDate(e.target.value); setCancelResult(null); }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          {cancelResult && (
            <Alert severity={cancelResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {cancelResult.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
            Close
          </Button>
          {!cancelResult?.success && (
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelConfirm}
              disabled={cancelLoading || !cancelStartDate || !cancelEndDate || cancelStartDate > cancelEndDate}
              startIcon={cancelLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
            >
              {cancelLoading ? 'Cancelling...' : 'Confirm Cancel'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {searched && (
        <TableContainer component={Paper}>
          {filteredReport.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="text.secondary">No data found for the selected range.</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Team Leader</strong></TableCell>
                  <TableCell><strong>Abbr</strong></TableCell>
                  <TableCell align="center"><strong>Team Size</strong></TableCell>
                  <TableCell align="center"><strong>Total Bookings</strong></TableCell>
                  <TableCell align="center"><strong>Total Days</strong></TableCell>
                  <TableCell><strong>First Booking</strong></TableCell>
                  <TableCell><strong>Last Booking</strong></TableCell>
                  <TableCell><strong>Booking Dates</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReport.map((row) => (
                  <TableRow key={row.user_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.color && (
                          <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: row.color, flexShrink: 0, border: '1px solid #ccc' }} />
                        )}
                        {row.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.abbreviation ? (
                        <Chip
                          label={row.abbreviation}
                          size="small"
                          sx={{
                            backgroundColor: row.color || '#ccc',
                            color: getContrastColor(row.color || '#ccc'),
                            fontWeight: 600,
                          }}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">{row.team_size || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.total_bookings || 0}
                        size="small"
                        color={row.total_bookings > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.total_days || 0}
                        size="small"
                        color={row.total_days > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {row.first_booking
                        ? format(new Date(row.first_booking), 'dd MMM yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {row.last_booking
                        ? format(new Date(row.last_booking), 'dd MMM yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', maxWidth: 300 }}>
                        {row.booking_dates
                          ? row.booking_dates.split(',').map(d => (
                              <Chip
                                key={d}
                                label={format(new Date(d), 'dd MMM')}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 22 }}
                              />
                            ))
                          : <Typography variant="caption" color="text.secondary">No bookings</Typography>
                        }
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default BookingReport;
